// ============================================================================
// IndicatorsCard — « Indicateurs · {mois} » (rail accueil E1a, mock fige)
// ============================================================================
// GsKpiComparison en heros (Revenu du mois + deltas budget / an. prec.) puis
// bande de 3 cellules stat inline (Benefice net ROUGE si negatif + micro-delta,
// Pulses repondues/total + humeur, Affichages ouverts) — les cellules ne sont
// PAS sur-componentisees (decision MASTER §3). Capability-aware : la carte est
// masquee sur 403 (employee) par AccueilView ; 503 = bandeau migration.
// Donnees vides (tables gest.* a 0 ligne) = valeurs « — » (layout stable).

import { GsKpiComparison, SkeletonCard, QueryError } from '@gs/ui';
import type { GsKpiComparisonDelta } from '@gs/ui';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import type { IndicatorsSummary, IndicatorComparison } from '../../hooks/useAccueil';
import {
  formatAmountDelta, formatCad, formatCompactCad, formatMonthLong, formatMood,
} from './format';
import { RailCard, NAV_LINK } from './RailCard';

const EMPTY = '—';
/** Pictogrammes de tendance (aria-hidden) — identiques en FR et en EN. */
const TREND_UP = '▲';
const TREND_DOWN = '▼';

function comparisonDeltas(cmp: IndicatorComparison, locale: string, t: TFunction): GsKpiComparisonDelta[] {
  const delta = (amount: number, ratio: number, label: string): GsKpiComparisonDelta => ({
    value: formatAmountDelta(amount, ratio, locale),
    label,
    direction: ratio >= 0 ? 'up' : 'down',
    favorable: ratio >= 0,
  });
  return [
    delta(cmp.deltaBudget, cmp.vsBudget, t('gest:accueil.indicators.vsBudget')),
    delta(cmp.deltaPriorYear, cmp.vsPriorYear, t('gest:accueil.indicators.vsPriorYear')),
  ];
}

export interface IndicatorsCardProps {
  data: IndicatorsSummary | undefined;
  isLoading: boolean;
  isError: boolean;
  migrationPending: boolean;
  onRetry: () => void;
  locale: string;
  t: TFunction;
  className?: string;
}

export function IndicatorsCard({
  data, isLoading, isError, migrationPending, onRetry, locale, t, className,
}: IndicatorsCardProps) {
  const navigate = useNavigate();
  if (isLoading) {
    return (
      <div className={className}>
        <SkeletonCard lines={5} />
      </div>
    );
  }

  const title = data?.month
    ? t('gest:accueil.indicators.monthTitle', { month: formatMonthLong(data.month, locale) })
    : t('gest:accueil.indicators.title');

  return (
    <RailCard
      title={title}
      className={className}
      trailing={
        // Drill-in navigation → brand (§4c « brand = wayfinding »), route /resultats.
        <button type="button" onClick={() => navigate('/resultats')} className={NAV_LINK}>
          {t('gest:accueil.indicators.detail')}
        </button>
      }
    >
      {migrationPending ? (
        <p className="text-field text-slate-400 dark:text-slate-500">{t('gest:accueil.migrationPending')}</p>
      ) : isError || !data ? (
        <QueryError message={t('gest:accueil.indicators.error')} onRetry={onRetry} />
      ) : (
        <>
          <GsKpiComparison
            label={t('gest:accueil.indicators.revenue')}
            value={data.revenue ? formatCad(data.revenue.value, locale) : EMPTY}
            negative={(data.revenue?.value ?? 0) < 0}
            deltas={data.revenue ? comparisonDeltas(data.revenue, locale, t) : []}
          />
          <div aria-hidden className="my-3.5 h-px bg-slate-100 dark:bg-slate-700/60" />
          <div className="grid grid-cols-3 gap-3">
            {/* Bénéfice net — ROUGE si négatif + micro-delta favorable vert */}
            <div>
              <p className="text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {t('gest:accueil.indicators.netProfit')}
              </p>
              <p
                className={`mt-1 text-head font-bold leading-none tabular-nums ${
                  (data.netProfit?.value ?? 0) < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-slate-800 dark:text-slate-100'
                }`}
              >
                {data.netProfit ? formatCompactCad(data.netProfit.value, locale) : EMPTY}
              </p>
              {data.netProfit && (
                <p
                  className={`mt-1 inline-flex items-baseline gap-0.5 text-meta font-semibold tabular-nums ${
                    data.netProfit.vsBudget >= 0
                      ? 'text-green-700 dark:text-green-500'
                      : 'text-red-600 dark:text-red-400'
                  }`}
                >
                  <span aria-hidden className="text-chip leading-none">
                    {data.netProfit.vsBudget >= 0 ? TREND_UP : TREND_DOWN}
                  </span>
                  {formatAmountDelta(data.netProfit.deltaBudget, data.netProfit.vsBudget, locale)}
                </p>
              )}
            </div>
            {/* Pulses — répondues/total + humeur */}
            <div>
              <p className="text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {t('gest:accueil.indicators.pulses')}
              </p>
              <p className="mt-1 text-head font-bold leading-none tabular-nums text-slate-800 dark:text-slate-100">
                {data.pulses.total > 0 ? `${data.pulses.answered}/${data.pulses.total}` : EMPTY}
              </p>
              <p className="mt-1 text-meta font-medium text-slate-400 dark:text-slate-500">
                {data.pulses.mood != null
                  ? t('gest:accueil.indicators.mood', { score: formatMood(data.pulses.mood, locale) })
                  : EMPTY}
              </p>
            </div>
            {/* Affichages — postes ouverts « à traiter » */}
            <div>
              <p className="text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {t('gest:accueil.indicators.postings')}
              </p>
              <p className="mt-1 text-head font-bold leading-none tabular-nums text-slate-800 dark:text-slate-100">
                {data.postings.count}
              </p>
              <p className="mt-1 text-meta font-medium text-slate-400 dark:text-slate-500">
                {t('gest:accueil.indicators.openPostings')}
              </p>
            </div>
          </div>
        </>
      )}
    </RailCard>
  );
}
